import { useState } from 'react';

export interface DashboardWidgetConfig {
  showSchedule: boolean;
  showStreak: boolean;
  showQuizMastery: boolean;
  showCharts: boolean;
  showPomodoro: boolean;
  showWeeklyReport: boolean;
  showGoalProgress: boolean;
  showRanking: boolean;
  showResearchActivity: boolean;
  showRoadmapProgress: boolean;
  showStudyStreakRanking: boolean;
  showMoodCorrelation: boolean;
  widgetOrder: string[];
  layoutMode: 'compact' | 'normal';
}

export const DEFAULT_WIDGET_CONFIG: DashboardWidgetConfig = {
  showSchedule: true,
  showStreak: true,
  showQuizMastery: true,
  showCharts: true,
  showPomodoro: false,
  showWeeklyReport: false,
  showGoalProgress: false,
  showRanking: false,
  showResearchActivity: false,
  showRoadmapProgress: false,
  showStudyStreakRanking: false,
  showMoodCorrelation: true,
  widgetOrder: [
    'schedule',
    'streak',
    'quizMastery',
    'charts',
    'moodCorrelation',
    'pomodoro',
    'weeklyReport',
    'studyGoal',
    'ranking',
    'streakRanking',
    'roadmap',
    'researchActivity',
  ],
  layoutMode: 'normal',
};

const STORAGE_KEY = 'dashboard_widget_config';

export const loadWidgetConfig = (): DashboardWidgetConfig => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<DashboardWidgetConfig>;
      return { ...DEFAULT_WIDGET_CONFIG, ...parsed };
    }
  } catch {
    // ignore
  }
  return { ...DEFAULT_WIDGET_CONFIG };
};

export const saveWidgetConfig = (config: DashboardWidgetConfig): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // ignore
  }
};

interface Props {
  config: DashboardWidgetConfig;
  onSave: (config: DashboardWidgetConfig) => void;
  onClose: () => void;
}

type WidgetBoolKey = keyof Omit<DashboardWidgetConfig, 'widgetOrder' | 'layoutMode'>;

interface WidgetInfo {
  id: WidgetBoolKey;
  label: string;
  description: string;
  icon: string;
  category: 'record' | 'analysis' | 'task' | 'ranking' | 'other';
}

const WIDGET_CATEGORIES = {
  record: { label: '学習記録', color: 'from-blue-500 to-cyan-500' },
  analysis: { label: '分析・統計', color: 'from-purple-500 to-pink-500' },
  task: { label: 'タスク管理', color: 'from-green-500 to-emerald-500' },
  ranking: { label: 'ランキング', color: 'from-orange-500 to-red-500' },
  other: { label: 'その他', color: 'from-gray-500 to-slate-500' },
};

const WIDGETS: WidgetInfo[] = [
  { id: 'showSchedule', label: '学習スケジュール', description: '今日の学習予定を確認', icon: 'ri-calendar-line', category: 'record' },
  { id: 'showStreak', label: '学習ストリーク', description: '連続学習日数を記録', icon: 'ri-fire-line', category: 'record' },
  { id: 'showQuizMastery', label: 'クイズ習熟度', description: '科目別の理解度を可視化', icon: 'ri-bar-chart-box-line', category: 'analysis' },
  { id: 'showCharts', label: '学習グラフ', description: '時間推移や科目別統計', icon: 'ri-line-chart-line', category: 'analysis' },
  { id: 'showMoodCorrelation', label: '気分×勉強時間', description: '気分と学習時間の相関分析', icon: 'ri-emotion-line', category: 'analysis' },
  { id: 'showPomodoro', label: 'ポモドーロタイマー', description: '集中学習をサポート', icon: 'ri-timer-line', category: 'task' },
  { id: 'showWeeklyReport', label: '週次レポート', description: '1週間の学習まとめ', icon: 'ri-file-chart-line', category: 'analysis' },
  { id: 'showGoalProgress', label: '学習目標達成率', description: '目標に対する進捗', icon: 'ri-trophy-line', category: 'task' },
  { id: 'showRanking', label: 'ランキング', description: '学習時間ランキング', icon: 'ri-medal-line', category: 'ranking' },
  { id: 'showResearchActivity', label: '研究活動', description: '研究進捗を追跡', icon: 'ri-flask-line', category: 'other' },
  { id: 'showRoadmapProgress', label: 'ロードマップ進捗', description: '研究ステップの達成状況', icon: 'ri-road-map-line', category: 'other' },
  { id: 'showStudyStreakRanking', label: 'ストリークランキング', description: '連続学習日数ランキング', icon: 'ri-fire-fill', category: 'ranking' },
];

export default function DashboardCustomizeModal({ config, onSave, onClose }: Props) {
  const [localConfig, setLocalConfig] = useState<DashboardWidgetConfig>(config);

  const handleToggle = (key: WidgetBoolKey) => {
    setLocalConfig(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCategoryToggle = (category: string, enable: boolean) => {
    const categoryWidgets = WIDGETS.filter(w => w.category === category);
    const updates: Partial<DashboardWidgetConfig> = {};
    categoryWidgets.forEach(widget => {
      (updates as Record<string, boolean>)[widget.id] = enable;
    });
    setLocalConfig(prev => ({ ...prev, ...updates }));
  };

  const handleSave = () => {
    onSave(localConfig);
    onClose();
  };

  const getActiveCount = () => WIDGETS.filter(w => localConfig[w.id]).length;

  const getCategoryCount = (category: string) => {
    const categoryWidgets = WIDGETS.filter(w => w.category === category);
    return {
      active: categoryWidgets.filter(w => localConfig[w.id]).length,
      total: categoryWidgets.length,
    };
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <i className="ri-settings-3-line text-3xl"></i>
                ダッシュボード表示設定
              </h2>
              <p className="text-teal-100 mt-2 text-sm">
                表示するウィジェットをカスタマイズして、あなた専用のダッシュボードを作成
              </p>
            </div>
            <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors cursor-pointer">
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
            <i className="ri-checkbox-circle-line text-lg"></i>
            <span className="font-semibold">{getActiveCount()}</span>
            <span className="text-sm">/ {WIDGETS.length} ウィジェット有効</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-8">
            {Object.entries(WIDGET_CATEGORIES).map(([categoryKey, categoryInfo]) => {
              const categoryWidgets = WIDGETS.filter(w => w.category === categoryKey);
              const counts = getCategoryCount(categoryKey);
              const allEnabled = counts.active === counts.total;
              const noneEnabled = counts.active === 0;

              return (
                <div key={categoryKey} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-1 h-8 rounded-full bg-gradient-to-b ${categoryInfo.color}`}></div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">{categoryInfo.label}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{counts.active} / {counts.total} 有効</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCategoryToggle(categoryKey, true)}
                        disabled={allEnabled}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${allEnabled ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50'}`}
                      >
                        <i className="ri-checkbox-multiple-line mr-1"></i>すべてON
                      </button>
                      <button
                        onClick={() => handleCategoryToggle(categoryKey, false)}
                        disabled={noneEnabled}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${noneEnabled ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50'}`}
                      >
                        <i className="ri-close-circle-line mr-1"></i>すべてOFF
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categoryWidgets.map(widget => {
                      const isActive = localConfig[widget.id];
                      return (
                        <button
                          key={widget.id}
                          onClick={() => handleToggle(widget.id)}
                          className={`relative p-4 rounded-xl border-2 transition-all text-left group cursor-pointer ${isActive ? `border-transparent bg-gradient-to-br ${categoryInfo.color} text-white shadow-lg` : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-md'}`}
                        >
                          <div className={`absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center transition-all ${isActive ? 'bg-white/30 backdrop-blur-sm' : 'bg-gray-100 dark:bg-gray-600 group-hover:bg-gray-200 dark:group-hover:bg-gray-500'}`}>
                            {isActive ? <i className="ri-check-line text-white font-bold"></i> : <i className="ri-add-line text-gray-400 dark:text-gray-300"></i>}
                          </div>
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${isActive ? 'bg-white/20 backdrop-blur-sm' : 'bg-gray-100 dark:bg-gray-600 group-hover:bg-gray-200 dark:group-hover:bg-gray-500'}`}>
                            <i className={`${widget.icon} text-2xl ${isActive ? 'text-white' : 'text-gray-600 dark:text-gray-300'}`}></i>
                          </div>
                          <h4 className={`font-bold mb-1 ${isActive ? 'text-white' : 'text-gray-800 dark:text-white'}`}>{widget.label}</h4>
                          <p className={`text-xs ${isActive ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>{widget.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <i className="ri-information-line mr-1"></i>変更は即座に反映されます
            </p>
            <div className="flex gap-3">
              <button onClick={onClose} className="px-6 py-2.5 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap">
                キャンセル
              </button>
              <button onClick={handleSave} className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-teal-600 to-emerald-600 text-white font-medium hover:from-teal-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap">
                <i className="ri-save-line mr-2"></i>保存する
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
